<script setup lang="ts">
import { ref, computed, watch } from "vue";
import BaseModal from "@/components/_elements/modals/BaseModal.vue";
import { useGlobalFilterStore, type ModalityOption } from "@/stores/globalFilterStore";
import { useUserStore } from "@/stores/userStore";
import { storeToRefs } from "pinia";
import { analytics } from "@/services/segment.ts";

const globalFilterStore = useGlobalFilterStore();
const { availableModalityOptions } = storeToRefs(globalFilterStore);

const userStore = useUserStore();
const { loaded } = storeToRefs(userStore);

const selecting = ref(false);

const isOpen = computed(
    () => loaded.value && !globalFilterStore.isSet && availableModalityOptions.value.length > 0
);

async function handleSelect(option: ModalityOption): Promise<void> {
    if (selecting.value) return;
    selecting.value = true;

    try {
        analytics.track("Learn Global Filter Area of Study Selected", {
            area_of_study: option.value,
        });

        await globalFilterStore.setFilter(option.value);
    } finally {
        selecting.value = false;
    }
}

watch(isOpen, (open) => {
    if (open) {
        analytics.track("Learn Global Filter Modal Shown");
    }
}, { immediate: true });
</script>

<template>
    <base-modal :opened="isOpen" max-width="4xl">
        <template #body>
            <div class="py-4 px-2 md:px-6 space-y-10 text-center">
                <div class="space-y-4">
                    <h2 class="text-2xl md:text-3xl font-bold text-gray-900">
                        Choose Your Area of Study
                    </h2>
                    <p class="text-gray-600 max-w-2xl mx-auto">
                        Thanks to our new Global Filter, your experience is now
                        <strong>more personalized than ever.</strong>
                        Select your area of study below to unlock your courses
                        and content — you can update this at any time in the top
                        navigation bar.
                    </p>
                </div>

                <div v-if="availableModalityOptions.length === 0" class="py-8 text-gray-500">
                    No courses found for your account. Please contact support if you believe this is an error.
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <button
                        v-for="option in availableModalityOptions"
                        :key="option.value"
                        type="button"
                        :disabled="selecting"
                        class="flex items-start gap-4 rounded-md bg-blue-50 p-5 text-left transition-colors hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        @click="handleSelect(option)"
                    >
                        <svg-icon
                            :icon="option.icon"
                            size="xl"
                            type="fill"
                            :class="['text-gray-900 shrink-0']"
                        ></svg-icon>
                        <div class="space-y-1">
                            <p class="font-bold text-gray-900 leading-snug">
                                {{ option.title }}
                            </p>
                            <p class="text-sm text-gray-600">
                                {{ option.description }}
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </template>
    </base-modal>
</template>
